// The relation of one leader's state to another, month by month. A month shows the relation of
// highest precedence (relations.ts: war, ally, denounced, friend, neutral) that held on any sampled
// day of it, so a war that began on the 15th shows from that month, and a war that began and ended
// inside one month is not lost.
//
//   war 2008-08-01 … 2008-08-16          2008-07  2008-08  2008-09
//                                          neutral   war     neutral
//
// Sampled days: each month end (the as-of month: the as-of date), and the days around every date
// a rule starts or stops in the window (a war's from/to, a sanction, treaty or membership span,
// a UCDP episode). The day before, the day itself and the day after cover both readings of `to`.

import type { DateStr, SourceId } from '../core/schema.ts';
import { addDays, fromDate } from '../core/dates.ts';
import type { Relation, RelationInfo } from './api.ts';
import type { Diplo } from './relations.ts';
import { byDate } from './util.ts';

export interface RelationMonth {
  /** 'YYYY-MM'. */
  month: string;
  relation: Relation;
  /** Why: the rule that fired on the day the month is shown for. */
  reason: string;
  sources: SourceId[];
}

const MONTH_CHARS = 7;

/** Precedence of the rules in relations.ts: the first that matches wins, so lower ranks are worse. */
const RANK: Record<Relation, number> = { self: 0, war: 1, ally: 2, denounced: 3, friend: 4, neutral: 5 };

/** Days around a rule date that can differ from the rest of the month: last day in, the date, first day out. */
const AROUND = [-1, 0, 1];

export function monthOf(date: DateStr): string {
  return date.slice(0, MONTH_CHARS);
}

/** The last day of each month from the month of `from` to the month of `to`; the final one is `to`. */
export function monthEnds(from: DateStr, to: DateStr): DateStr[] {
  const last = monthOf(to);
  const out: DateStr[] = [];
  let year = Number(from.slice(0, 4));
  let month = Number(from.slice(5, MONTH_CHARS));

  while (true) {
    // Day 0 of the next month is the last day of this one.
    const end = fromDate(new Date(Date.UTC(year, month, 0)));
    if (monthOf(end) >= last) {
      out.push(to);
      return out;
    }

    out.push(end);
    month++;
    if (month > 12) {
      month = 1;
      year++;
    }
  }
}

export function monthEntry(date: DateStr, info: RelationInfo): RelationMonth {
  return { month: monthOf(date), relation: info.relation, reason: info.reason, sources: info.sources };
}

/** Every date a rule of the relations starts or stops, as written in the curated and UCDP records. */
function ruleDates(d: Diplo): DateStr[] {
  const out: Array<DateStr | null> = [];
  for (const w of d.wars) {
    out.push(w.from, w.to, w.status.as_of);
    for (const p of w.party_spans ?? []) {
      out.push(p.from, p.to);
    }
  }
  for (const list of [d.treaties, d.sanctions, ...d.byMember.values()]) {
    for (const x of list) {
      out.push(x.from, x.to);
    }
  }
  for (const list of d.dyads.values()) {
    for (const y of list) {
      out.push(y.start, y.end);
    }
  }
  return out.filter((x): x is DateStr => x !== null);
}

/** The days a history is sampled on within [from, to]: month ends, and the days around each rule date. Ascending. */
export function sampleDates(d: Diplo, from: DateStr, to: DateStr): DateStr[] {
  const days = new Set<DateStr>(monthEnds(from, to));
  for (const rule of ruleDates(d)) {
    for (const k of AROUND) {
      const day = addDays(rule, k);
      if (day >= from && day <= to) {
        days.add(day);
      }
    }
  }
  return [...days].sort(byDate);
}

/**
 * One entry per month from samples in ascending date order: the sample whose relation ranks first;
 * the later day wins a tie, so a month without a spell keeps the reason of its last day.
 */
export function foldMonths(samples: Array<{ date: DateStr; info: RelationInfo }>): RelationMonth[] {
  const out: RelationMonth[] = [];
  for (const s of samples) {
    const last = out[out.length - 1];
    if (!last || last.month !== monthOf(s.date)) {
      out.push(monthEntry(s.date, s.info));
      continue;
    }
    if (RANK[s.info.relation] <= RANK[last.relation]) {
      out[out.length - 1] = monthEntry(s.date, s.info);
    }
  }
  return out;
}
